import { readonly, ref } from 'vue'
import { useInsuranceStore } from '@/libs/api/insurance/insurance.store.ts'
import { useConnectStore } from '@/libs/api/connect/connect.store.ts'
import type {
  ProductOfferResponse,
  ProductSpecificationResponse
} from '@/libs/api/insurance/insurance.types.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import type { LValidationResult } from '@/libs/validation/components/LValidationForm.vue'
import { useAuthStore } from '@/stores/auth.store'
import type { useGlobalErrorService } from '@/services/error.service'
import { translate } from '@/i18n/i18n'

export type TariffState = {
  deviceClassId?: string
  purchaseDate?: string
  purchasePrice?: string
}

type ProductOfferWithDeviceClass = ProductOfferResponse & { deviceClassId: string }

export const useDefaultVariantForm = (errorService: ReturnType<typeof useGlobalErrorService>) => {
  const uiStore = useUiStore()
  const insuranceStore = useInsuranceStore()
  const connectStore = useConnectStore()
  const authStore = useAuthStore()

  const validationResult = ref<LValidationResult>({
    isValid: false,
    hasError: false,
    isPristine: true,
    isDirty: false
  })

  // current form state, needed because EntryForm requieres a formData v-model property
  const formState = ref<TariffState>()
  // last used form state for fetching offers and product specifications
  const latestTariffState = ref<TariffState>()

  const currentOffers = ref<ProductOfferWithDeviceClass[]>([])
  const currentProductSpecs = ref<Record<string, ProductSpecificationResponse>>({})

  const updateOffers = async (
    newData: TariffState | undefined
  ): Promise<ProductOfferResponse[]> => {
    const { deviceClassId } = newData ?? {}

    if (deviceClassId) {
      const deviceClassIds = [deviceClassId]
      // if accumulator is also available for given device class id add it to the offers
      if (connectStore.hasAccumulatorOffer(deviceClassId))
        deviceClassIds.push(connectStore.batteryDeviceId)

      // get cached offers for device classes and filter remove values
      const offers = deviceClassIds.flatMap((deviceClassId) => {
        const offersForDeviceClass = getOffers(deviceClassId)
        return offersForDeviceClass.map((offer) => ({ ...offer, deviceClassId }))
      })
      setCurrentOffers(offers)
      return offers
    }

    setCurrentOffers([])
    return []
  }

  const fetchCurrentProductSpecs = async () => {
    const specsCalls = currentOffers.value.map(async (offer) => ({
      id: offer.id,
      specs: await insuranceStore.loadProductSpec({
        product_id: offer.id,
        device_class_id: offer.deviceClassId,
        purchase_date: latestTariffState.value?.purchaseDate,
        purchase_price: latestTariffState.value?.purchasePrice
      })
    }))
    const specCallResults = await Promise.allSettled(specsCalls)
    const result = specCallResults.reduce(
      (result, current) =>
        current.status === 'fulfilled'
          ? { ...result, [current.value.id]: current.value.specs }
          : result,
      {}
    )
    setCurrentProductSpecs(result)
  }

  const resetProductSpec = () => {
    currentProductSpecs.value = {}
    uiStore.setLoading(false)
  }

  const setCurrentProductSpecs = (value: Record<string, ProductSpecificationResponse>) => {
    currentProductSpecs.value = value
  }

  const getOffers = (deviceClassId: string) => {
    const offers = insuranceStore.getDeviceClassOffers(deviceClassId)

    if (!offers.length) {
      console.warn(`No offers found for device class ID: ${deviceClassId}`)
    }

    return offers
  }
  const setCurrentOffers = (value: ProductOfferWithDeviceClass[]) => {
    currentOffers.value = value
  }

  const handleFormDataUpdate = async (formData: TariffState | undefined) => {
    const currentTariffState = latestTariffState.value
    // reset offers and product specifications if any value has changed
    if (
      formData?.deviceClassId !== currentTariffState?.deviceClassId ||
      formData?.purchaseDate !== currentTariffState?.purchaseDate ||
      formData?.purchasePrice !== currentTariffState?.purchasePrice
    ) {
      await updateOffers(undefined)
      resetProductSpec()
    }

    latestTariffState.value = formData
  }

  // function for invoking fetching of offers and related product specifications based on latestTariffState
  const handleShowOffers = async () => {
    const tariff = latestTariffState.value

    if (!authStore.isAuthenticated()) {
      await authStore.authenticate()
    }

    if (validationResult.value.isValid) {
      try {
        uiStore.clearError()

        await updateOffers(tariff)

        await fetchCurrentProductSpecs()
      } catch (error) {
        handleError(error)
      }
    } else {
      await updateOffers(tariff)
      resetProductSpec()
    }
    uiStore.setLoading(false)
  }

  const handleError = (error: unknown) => {
    resetProductSpec()
    errorService.handleError(error)
  }

  const handleValidationResult = (result: LValidationResult) => {
    if (result.hasError) {
      showInputError()
    } else {
      uiStore.clearError()
    }
  }

  const handleOnFormUpdate = (data: boolean) => {
    uiStore.setLoading(data)
  }

  const showInputError = () => uiStore.setError(translate('errors.check_input'))

  return {
    latestTariffState: readonly(latestTariffState),
    formState,
    currentOffers,
    currentProductSpecs,
    updateOffers,
    fetchCurrentProductSpecs,
    resetProductSpec,
    handleShowOffers,
    handleValidationResult,
    handleOnFormUpdate,
    handleFormDataUpdate,
    validationResult
  }
}
